/**
 * IN SHORT: This is the 'Error Registry'. It defines specific, secure error types
 * so the app can handle problems (like expired tokens) gracefully without leaking secrets.
 *
 * SDK Error Hierarchy
 *
 * Security principle: error messages must NOT leak cryptographic material,
 * key IDs from untrusted inputs, or internal state that aids an attacker.
 * Keep messages generic enough to be safe yet specific enough for debugging.
 */

export enum JwtSdkErrorCode {
  // Key lifecycle
  KEY_GENERATION_FAILED = 'KEY_GENERATION_FAILED',
  KEY_IMPORT_FAILED = 'KEY_IMPORT_FAILED',
  KEY_EXPORT_FAILED = 'KEY_EXPORT_FAILED',

  // Cryptographic operations
  SIGNING_FAILED = 'SIGNING_FAILED',
  VERIFICATION_FAILED = 'VERIFICATION_FAILED',
  ENCRYPTION_FAILED = 'ENCRYPTION_FAILED',
  DECRYPTION_FAILED = 'DECRYPTION_FAILED',
  INVALID_TOKEN = 'INVALID_TOKEN',
  ALGORITHM_MISMATCH = 'ALGORITHM_MISMATCH',

  // Security / claims validation
  REPLAY_DETECTED = 'REPLAY_DETECTED',
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  INVALID_CLAIMS = 'INVALID_CLAIMS',

  // Environment
  WEBCRYPTO_UNAVAILABLE = 'WEBCRYPTO_UNAVAILABLE',
}

/**
 * Base SDK error — always carries a typed code so callers can switch/match
 * without parsing error message strings.
 */
export class JwtSdkError extends Error {
  readonly code: JwtSdkErrorCode;
  override readonly cause?: unknown;

  constructor(code: JwtSdkErrorCode, message: string, cause?: unknown) {
    super(message);
    this.name = 'JwtSdkError';
    this.code = code;
    this.cause = cause;
    // Restore prototype chain so `instanceof JwtSdkError` works after transpilation.
    Object.setPrototypeOf(this, new.target.prototype);
  }

  static wrap(code: JwtSdkErrorCode, message: string, raw: unknown): JwtSdkError {
    const cause = raw instanceof Error ? raw : new Error(String(raw));
    return new JwtSdkError(code, message, cause);
  }
}

export class ReplayAttackError extends JwtSdkError {
  constructor() {
    // Do NOT include the JTI value: that would confirm to an attacker which JTIs are tracked.
    super(JwtSdkErrorCode.REPLAY_DETECTED, 'Token replay detected. The presented JWT ID has already been used.');
    this.name = 'ReplayAttackError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class TokenExpiredError extends JwtSdkError {
  constructor() {
    super(JwtSdkErrorCode.TOKEN_EXPIRED, 'Token has expired.');
    this.name = 'TokenExpiredError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class WebCryptoUnavailableError extends JwtSdkError {
  constructor() {
    super(
      JwtSdkErrorCode.WEBCRYPTO_UNAVAILABLE,
      'Web Crypto API is not available. Ensure you are running in a secure context (HTTPS or localhost).',
    );
    this.name = 'WebCryptoUnavailableError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class AlgorithmMismatchError extends JwtSdkError {
  constructor(expected: string, received: string) {
    super(
      JwtSdkErrorCode.ALGORITHM_MISMATCH,
      `Algorithm mismatch: expected ${expected}, received ${received}. Possible algorithm substitution attack.`,
    );
    this.name = 'AlgorithmMismatchError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}
